import { HandleNegativeOnHand } from "@redotech/cockroachdb-codegen/tables/inventory/handle-negative-on-hand";
import { ImsCostingMethod } from "@redotech/cockroachdb-codegen/tables/inventory/ims-costing-method";
import { ImsStatus } from "@redotech/cockroachdb-codegen/tables/inventory/ims-status";
import {
  CollectionMethod,
  PaymentProviderType,
} from "@redotech/redo-billing-common/billing-engine-types";
import { BillingPeriod } from "@redotech/redo-billing-common/service-invoice";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { recommendationUrgencySchema } from "./purchase-order-recommendations";

/**
 * Mirrors the shared Mongoose subschema
 * (`getSubscriptionBillingSchemaDefinition`). Every field it stores has to be
 * here: this schema is parsed and the result written back as a whole
 * subdocument, in the admin settings save, both `disableBilling` paths, the
 * Shopify subscription teardown, and the CRDB mirror row. Zod strips what it
 * does not declare, and the subschema's defaults then fill the gap — so an
 * omission does not leave a field absent, it resets it.
 */
export const imsSubscriptionBillingSettingsSchema = z.object({
  enabled: z.boolean(),
  renewOnPeriodEnd: z.boolean(),
  startDate: z.coerce.date().nullish(),
  period: createEnum(BillingPeriod, { enumName: "BillingPeriod" }),
  periodFeeInCents: z.number().int().nonnegative(),
  paymentProvider: createEnum(PaymentProviderType, {
    enumName: "PaymentProviderType",
  }).optional(),
  collectionMethod: createEnum(CollectionMethod, {
    enumName: "CollectionMethod",
  }).nullish(),
  netTermsDays: z.number().int().min(0).nullish(),
});
export type ImsSubscriptionBillingSettings = z.infer<
  typeof imsSubscriptionBillingSettingsSchema
>;

export const imsForecastingBillingSettingsSchema =
  imsSubscriptionBillingSettingsSchema.extend({
    useSubscriptionPricing: z.boolean().optional(),
    customPriceInCents: z.number().int().nonnegative().nullish(),
  });
export type ImsForecastingBillingSettings = z.infer<
  typeof imsForecastingBillingSettingsSchema
>;

export const ImsSettingsSchema = z.object({
  enabled: z.boolean(),
  syncEnabled: z.boolean(),
  forecastingEnabled: z.boolean(),
  status: createEnum(ImsStatus, { enumName: "ImsStatus" }),
  sourceOfTruthSince: z.date().nullable(),
  adjustmentSyncAt: z.date().nullable(),
  lastForecastAt: z.date().nullable(),
  catalogEnabled: z.boolean(),
  catalogSourceOfTruthStatus: createEnum(ImsStatus, { enumName: "ImsStatus" }),
  catalogSourceOfTruthSince: z.date().nullable(),
  productSyncVersion: z.number().int().nullable(),
  lastPushToShopifyAt: z.date().nullable(),
  subscriptionBilling: imsSubscriptionBillingSettingsSchema.nullable(),
  forecastingBilling: imsForecastingBillingSettingsSchema.nullable(),
  selfEnroll: z.boolean().nullable(),
  inventoryBackfillComplete: z.boolean(),
  inventoryBackfillAt: z.date().nullable(),
  orderSyncTemporal: z.boolean(),
  purchaseOrderDefaultLocationId: locationIdFormat.schema.nullable(),
  purchaseOrderDefaultCcEmails: z.array(z.string()),
  purchaseOrderRecommendationUrgencies: z.array(recommendationUrgencySchema),
  cycleCountVariancePercent: z.number().int().min(0).max(100).nullable(),
  cycleCountVarianceAmount: z.number().int().nonnegative().nullable(),
  handleNegativeOnHand: createEnum(HandleNegativeOnHand, {
    enumName: "HandleNegativeOnHand",
  }).nullable(),
  costingMethod: createEnum(ImsCostingMethod, { enumName: "ImsCostingMethod" }),
});
export type CanonicalImsSettings = z.infer<typeof ImsSettingsSchema>;

export const ImsSettingsUpdateSchema = ImsSettingsSchema.partial();
export type ImsSettingsUpdate = z.input<typeof ImsSettingsUpdateSchema>;

/**
 * Inventory Management System (IMS) settings.
 *
 * Compatibility shape used by Team responses while persistence moves to CRDB.
 * New backend code should use `CanonicalImsSettings`.
 */
export interface ImsSettings {
  enabled: boolean;
  syncEnabled: boolean;
  status: ImsStatus | null;
  sourceOfTruthSince?: Date | null;
  adjustmentSyncAt?: Date | null;
  forecastingEnabled?: boolean;
  lastForecastAt?: Date | null;
  catalogEnabled?: boolean;
  catalogSourceOfTruthStatus?: ImsStatus | null;
  catalogSourceOfTruthSince?: Date | null;
  productSyncVersion?: number | null;
  lastPushToShopifyAt?: Date | null;
  subscriptionBilling?: ImsSubscriptionBillingSettings;
  forecastingBilling?: ImsForecastingBillingSettings;
  selfEnroll?: boolean | null;
  inventoryBackfillComplete?: boolean | null;
  inventoryBackfillAt?: Date | null;
  orderSyncTemporal?: boolean;
  purchaseOrderDefaultLocationId?: string | null;
  purchaseOrderDefaultCcEmails?: string[];
  purchaseOrderRecommendationUrgencies?: string[];
  cycleCountVariancePercent?: number | null;
  cycleCountVarianceAmount?: number | null;
  handleNegativeOnHand?: HandleNegativeOnHand | null;
  costingMethod?: ImsCostingMethod;
  defaultPoTemplateId?: string;
}
